import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, firstValueFrom, merge, of, Subject } from 'rxjs';
import {
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { Customers } from 'server/shared/collections/customers';
import { Jobs } from 'server/shared/collections/jobs';
import { getJobCounts, JobCounts } from 'server/shared/helpers/getJobCounts';
import { filterMultidayOverviewInvitees } from 'server/shared/helpers/job/filterOverviewInvitees';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { JobEditService } from 'src/app/services/job-edit.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { getPossibleUsersForJob } from 'src/helpers/job/getPossibleUsersForJob';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { param$ } from 'src/helpers/queryParam$';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PoolerService } from '../../services/pooler/pooler.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';
import { UnsavedChangesDeactivate } from '../../shared/guards/unsaved-changes-deactivate.interface';
import {
  InviteeToggledEvent,
  JobMatchPoolerUser,
} from '../job-edit/job-edit.types';

type MultiDayJobSummary = {
  job: Job;
  jobCounts: JobCounts;
  jobCustomer: Customer;
  overviewInvitees: Invitee[];
  possibleUsers: JobMatchPoolerUser[];
  directInvitedUsers: JobMatchPoolerUser[];
  directAcceptedUsers: JobMatchPoolerUser[];
  jobInPast: boolean;
  suggestInsteadDirectAccept: boolean;
  collapsed: boolean;
};

type PreparedJob = {
  job: Job;
  possibleUsers: JobMatchPoolerUser[];
  customerOfJob: Customer;
};

type SelectionEvent = {
  eventType: 'USER_TOGGLE';
  userId: string;
  jobId: string;
  type: 'INVITE' | 'DIRECT_ACCEPT';
  selected: boolean;
};

type SelectAllEvent = {
  eventType: 'SELECT_ALL';
  jobId: string;
  userIds: string[];
  type: 'INVITE' | 'DIRECT_ACCEPT';
  selected: boolean;
};

type ClearAllEvent = {
  eventType: 'CLEAR_ALL';
};

type SelectionEvents = SelectionEvent | SelectAllEvent | ClearAllEvent;

type JobSelections = {
  inviteUserIds: Set<string>;
  bookUserIds: Set<string>;
};

// state by job-id
type SelectionState = Map<string, JobSelections>;

@Component({
  selector: 'job-multiday-edit',
  templateUrl: 'job-multiday-edit.component.html',
  styleUrls: ['job-multiday-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobMultidayEditComponent
  implements OnDestroy, UnsavedChangesDeactivate
{
  private destroyed$ = new Subject<void>();

  i18n = this.translationService.getTranslations('jobs');

  inviteeStatusEnum = InviteeStatusEnum;
  jobStatusEnum = JobStatusEnum;
  roleEnum = RoleEnum;

  multiDayId$ = param$(this.route, 'multiDayId');

  loggedInUser$ = this.userService.readyDispo$.pipe(takeUntil(this.destroyed$));

  // Properties for job-possible-users-list component
  loggedInCustomer$ = this.customerService.readyDispoCustomer$;

  multiDayJobs$ = this.multiDayId$.pipe(
    switchMap((multiDayId) => {
      if (!multiDayId) {
        return of<Job[]>([]);
      }
      return Jobs.find(
        {
          multiDayId: multiDayId,
          // to support other status we would need to subscribe canceled & archived jobs in this view or subscribe jobs by id
          // removing the filter leads to consistency issues depending on initialized subscriptions
          status: { $nin: [JobStatusEnum.CANCELED, JobStatusEnum.ARCHIVED] },
        },
        {},
      );
    }),
    map((jobs) =>
      jobs.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    ),
  );

  department$ = combineLatest([
    this.multiDayJobs$,
    this.organisationService.readyDepartments$,
  ]).pipe(
    map(([jobs, departments]) => {
      return departments.find((department) =>
        jobs.some((job) => job.department.id === department.id),
      );
    }),
    takeUntil(this.destroyed$),
  );

  preparedJobs$ = combineLatest([
    this.multiDayJobs$,
    this.poolerService.readyJobSharedPoolCustomers$,
    this.customerService.readyDispoCustomer,
    this.loggedInUser$,
    this.poolerService.readyPoolerCustomerMap$,
    this.subscriptionService.jobs.ready$,
    this.subscriptionService.jobUsersReady$,
    this.department$,
  ]).pipe(
    switchMap(
      ([
        jobs,
        sharedPoolCustomers,
        customer,
        loggedInUser,
        poolerMap,
        jobsReady,
        usersReady,
        department,
      ]) => {
        if (
          !customer ||
          !loggedInUser ||
          !poolerMap ||
          !jobsReady ||
          !usersReady ||
          !department
        ) {
          return of(
            jobs.map(
              (job): PreparedJob => ({
                job,
                possibleUsers: [],
                customerOfJob: customer,
              }),
            ),
          );
        }
        return observeTracker(() => {
          return jobs.map((job: Job): PreparedJob => {
            const customer = Customers.findOne({
              _id: job.customer.id,
            });
            const possibleUsers =
              job.shifts &&
              job.shifts.length > 0 &&
              job.status === JobStatusEnum.OPEN
                ? getPossibleUsersForJob({
                    date: job.date,
                    job: job,
                    shifts: job.shifts,
                    department: department,
                    loggedInCustomer: customer,
                    sharedPoolCustomers: sharedPoolCustomers,
                    poolerMap: poolerMap,
                    user: loggedInUser,
                    i18n: this.i18n,
                    isOwnJob: job.customer.id === customer._id,
                    undecided: false,
                    ignoreUserPreferenceDepartmentBlacklist: false,
                    jobInPast: new Date(job.date) < new Date(),
                  })
                : [];

            return {
              job,
              possibleUsers,
              customerOfJob: customer,
            };
          });
        });
      },
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  userCount$ = this.preparedJobs$.pipe(
    map((preparedJobs) => {
      const userCountMap = new Map<string, number>();
      preparedJobs.forEach((preparedJob) => {
        const job = preparedJob.job;
        // Count possible users from possibleUsers array
        preparedJob.possibleUsers.forEach((user) => {
          const currentCount = userCountMap.get(user._id) || 0;
          userCountMap.set(user._id, currentCount + 1);
        });
        // Count invitees that are open, booked, or available
        if (job.invitees) {
          job.invitees
            .filter(
              (invitee): invitee is Invitee & { userId: string } =>
                !!invitee.userId,
            )
            .forEach((invitee) => {
              if (
                [
                  InviteeStatusEnum.OPEN,
                  InviteeStatusEnum.BOOKED,
                  InviteeStatusEnum.AVAILABLE,
                ].includes(invitee.status)
              ) {
                const currentCount = userCountMap.get(invitee.userId) || 0;
                userCountMap.set(invitee.userId, currentCount + 1);
              }
            });
        }
      });

      return userCountMap;
    }),
    shareReplay(1),
  );

  // Selection event subjects
  private selectionEvent$ = new Subject<SelectionEvent>();
  private selectAllEvent$ = new Subject<SelectAllEvent>();
  private clearAllEvent$ = new Subject<ClearAllEvent>();

  private toggleUserForSelection(
    selectionState: SelectionState,
    event: SelectionEvent,
  ): SelectionState {
    const newState = new Map(selectionState);
    const { userId, jobId, type, selected } = event;

    // Get or create job selections
    let jobSelections = newState.get(jobId);
    if (!jobSelections) {
      jobSelections = {
        inviteUserIds: new Set<string>(),
        bookUserIds: new Set<string>(),
      };
    } else {
      // Clone the sets to avoid mutation
      jobSelections = {
        inviteUserIds: new Set(jobSelections.inviteUserIds),
        bookUserIds: new Set(jobSelections.bookUserIds),
      };
    }

    if (selected) {
      // Remove from the opposite type to ensure mutual exclusivity
      if (type === 'INVITE') {
        jobSelections.bookUserIds.delete(userId);
        jobSelections.inviteUserIds.add(userId);
      } else {
        jobSelections.inviteUserIds.delete(userId);
        jobSelections.bookUserIds.add(userId);
      }
    } else {
      // Remove from the current type
      if (type === 'INVITE') {
        jobSelections.inviteUserIds.delete(userId);
      } else {
        jobSelections.bookUserIds.delete(userId);
      }
    }

    // Clean up empty job selections
    if (
      jobSelections.inviteUserIds.size === 0 &&
      jobSelections.bookUserIds.size === 0
    ) {
      newState.delete(jobId);
    } else {
      newState.set(jobId, jobSelections);
    }
    return newState;
  }

  private toggleSelectAll(
    selectionState: SelectionState,
    event: SelectAllEvent,
  ): SelectionState {
    const newState = new Map(selectionState);
    const { jobId, userIds, type, selected } = event;

    if (selected) {
      const userIdsSet = new Set(userIds);
      const jobSelections: JobSelections = {
        inviteUserIds: type === 'INVITE' ? userIdsSet : new Set<string>(),
        bookUserIds: type === 'DIRECT_ACCEPT' ? userIdsSet : new Set<string>(),
      };
      newState.set(jobId, jobSelections);
    } else {
      newState.delete(jobId);
    }

    return newState;
  }

  // Selection state observable using merge instead of combineLatest
  selectionByJobId$ = merge(
    this.selectionEvent$,
    this.selectAllEvent$,
    this.clearAllEvent$,
  ).pipe(
    scan((selectionState: SelectionState, event: SelectionEvents) => {
      switch (event.eventType) {
        case 'CLEAR_ALL':
          return new Map<string, JobSelections>();

        case 'SELECT_ALL':
          return this.toggleSelectAll(selectionState, event);

        case 'USER_TOGGLE':
          return this.toggleUserForSelection(selectionState, event);

        default:
          return selectionState;
      }
    }, new Map<string, JobSelections>()),
    startWith(new Map<string, JobSelections>()),
    takeUntil(this.destroyed$),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  multiDayJobSummaries$ = combineLatest([
    this.preparedJobs$,
    this.poolerService.readyJobSharedPoolCustomers$,
    this.customerService.readyDispoCustomer,
    this.selectionByJobId$,
    this.userCount$,
  ]).pipe(
    scan(
      (
        previousSummaries: MultiDayJobSummary[],
        [
          preparedJobs,
          sharedPoolCustomers,
          customer,
          selectionByJobId,
          userCountMap,
        ],
      ): MultiDayJobSummary[] => {
        return preparedJobs.map((preparedJob: PreparedJob) => {
          const previousSummary = previousSummaries.find(
            (summary) => summary.job._id === preparedJob.job._id,
          );
          return this.createJobSummary(
            preparedJob,
            previousSummary,
            customer,
            sharedPoolCustomers,
            selectionByJobId,
            userCountMap,
          );
        });
      },
      [] as MultiDayJobSummary[],
    ),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  loading$ = this.subscriptionService.jobs.ready$.pipe(
    map((ready) => !ready),
    startWith(true),
    rxNgZoneRun(this.ngZone),
  );

  // Derived observables
  selectionCounts$ = this.selectionByJobId$.pipe(
    map((selectionState) => {
      let inviteCount = 0;
      let bookCount = 0;

      selectionState.forEach((selections) => {
        inviteCount += selections.inviteUserIds.size;
        bookCount += selections.bookUserIds.size;
      });

      const totalCount = inviteCount + bookCount;

      return {
        inviteCount,
        bookCount,
        total: totalCount,
      };
    }),
  );

  hasSelections$ = this.selectionCounts$.pipe(
    map((counts) => counts.total > 0),
  );

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private translationService: TranslationService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService,
    private userService: UserService,
    private poolerService: PoolerService,
    private route: ActivatedRoute,
    private jobService: JobEditService,
    private toastService: ToastService,
    private organisationService: OrganisationService,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.selectionEvent$.complete();
    this.selectAllEvent$.complete();
    this.clearAllEvent$.complete();
  }

  getStatusTooltip(summary: MultiDayJobSummary): string {
    const counts = summary.jobCounts;

    if (counts.open === 0 && counts.booked < counts.requiredUsers) {
      return this.i18n?.noOpenInvitations || 'No open invitations';
    } else if (counts.booked >= counts.requiredUsers) {
      return this.i18n?.booked || 'Booked';
    } else if (counts.available > 0) {
      return this.i18n?.hasAvailablePeople || 'Has available people';
    } else {
      return this.i18n?.waitingForResponse || 'Waiting for response';
    }
  }

  editSingleJob(jobId: string) {
    this.router.navigate(['/jobs', jobId]).then();
  }

  goBackToJobs() {
    this.router.navigate(['/jobs']).then();
  }

  trackByJobId(_: number, summary: MultiDayJobSummary): string {
    return summary.job._id;
  }

  toggleJobCollapsed(summary: MultiDayJobSummary, collapsed: boolean) {
    summary.collapsed = collapsed;
  }

  onInviteeToggled(event: InviteeToggledEvent, job: Job) {
    this.selectionEvent$.next({
      eventType: 'USER_TOGGLE',
      userId: event.user._id,
      jobId: job._id,
      type: event.type,
      selected: event.inviteeSelected,
    });
  }

  onSelectAllToggled(
    selected: boolean,
    job: Job,
    possibleUsers: JobMatchPoolerUser[],
  ) {
    this.selectAllEvent$.next({
      eventType: 'SELECT_ALL',
      jobId: job._id,
      userIds: possibleUsers.map((user) => user._id),
      type: 'INVITE',
      selected,
    });
  }

  isUserSelectedForJob(userId: string, jobId: string): boolean {
    let isSelected = false;
    this.selectionByJobId$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selectionState) => {
        const jobSelections = selectionState.get(jobId);
        if (jobSelections) {
          isSelected =
            jobSelections.inviteUserIds.has(userId) ||
            jobSelections.bookUserIds.has(userId);
        }
      })
      .unsubscribe();
    return isSelected;
  }

  clearAllSelections() {
    this.clearAllEvent$.next({ eventType: 'CLEAR_ALL' });
  }

  hasUnsavedChanges(): boolean {
    // Check if there are any selections in the current state
    // We subscribe briefly to get the current value synchronously
    let hasSelections = false;
    const subscription = this.selectionByJobId$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        hasSelections = state.size > 0;
      });
    subscription.unsubscribe();
    return hasSelections;
  }

  private hasTooManyBookedUsers(
    selectionState: SelectionState,
    preparedJobs: PreparedJob[],
  ): boolean {
    for (const [jobId, jobSelections] of selectionState) {
      const preparedJob = preparedJobs.find((p) => p.job._id === jobId);
      if (!preparedJob) continue;

      // Count existing booked invitees
      const existingBookedCount =
        preparedJob.job.invitees?.filter(
          (invitee) => invitee.status === InviteeStatusEnum.BOOKED,
        )?.length || 0;

      const newBookedCount = jobSelections.bookUserIds.size;
      const totalBookedCount = existingBookedCount + newBookedCount;

      if (totalBookedCount > preparedJob.job.requiredUsers) {
        return true;
      }
    }

    return false;
  }

  async submitSelection() {
    try {
      // Get current selection state
      const currentSelections = await firstValueFrom(
        this.selectionByJobId$.pipe(takeUntil(this.destroyed$)),
      );

      if (!currentSelections || currentSelections.size === 0) {
        return;
      }

      // Get required data
      const [loggedInCustomer, sharedPoolCustomers, preparedJobs] =
        await Promise.all([
          firstValueFrom(
            this.loggedInCustomer$.pipe(takeUntil(this.destroyed$)),
          ),
          firstValueFrom(
            this.poolerService.readyJobSharedPoolCustomers$.pipe(
              takeUntil(this.destroyed$),
            ),
          ),
          firstValueFrom(this.preparedJobs$.pipe(takeUntil(this.destroyed$))),
        ]);

      if (!loggedInCustomer || !sharedPoolCustomers || !preparedJobs) {
        console.error('Required data not available for submission');
        this.toastService.add({
          type: 'ERROR',
          message: 'serverError',
        });
        return;
      }

      // Validate booked user counts before proceeding with submission
      if (this.hasTooManyBookedUsers(currentSelections, preparedJobs)) {
        // Show error toast and return early to prevent job updates
        this.toastService.add({
          type: 'ERROR',
          message: 'InvalidForm',
        });
        return;
      }

      let totalInviteCount = 0;
      let totalBookCount = 0;

      // Process each job with selections
      for (const [jobId, jobSelections] of currentSelections) {
        if (
          jobSelections.inviteUserIds.size === 0 &&
          jobSelections.bookUserIds.size === 0
        ) {
          continue;
        }

        // Find the prepared job
        const preparedJob = preparedJobs.find((p) => p.job._id === jobId);
        if (!preparedJob) {
          console.error(`Job with ID ${jobId} not found in prepared jobs`);
          continue;
        }

        // Get users for invitation and direct accept
        const inviteUsers = Array.from(jobSelections.inviteUserIds)
          .map((userId) =>
            preparedJob.possibleUsers.find((u) => u._id === userId),
          )
          .filter((user): user is JobMatchPoolerUser => user !== undefined);

        const directAcceptUsers = Array.from(jobSelections.bookUserIds)
          .map((userId) =>
            preparedJob.possibleUsers.find((u) => u._id === userId),
          )
          .filter((user): user is JobMatchPoolerUser => user !== undefined);

        // Count the users for success message
        totalInviteCount += inviteUsers.length;
        totalBookCount += directAcceptUsers.length;

        // Create invitees for this job
        const newInvitees = [
          ...directAcceptUsers.map((user) =>
            this.jobService.createInviteeForUser(
              user,
              'DIRECT_ACCEPT',
              loggedInCustomer,
              sharedPoolCustomers,
              preparedJob.job.transfers?.find(
                (t) => t.targetCustomer.id === loggedInCustomer._id,
              )?.preventDirectAccept || false,
            ),
          ),
          ...inviteUsers.map((user) =>
            this.jobService.createInviteeForUser(
              user,
              'INVITE',
              loggedInCustomer,
              sharedPoolCustomers,
              preparedJob.job.transfers?.find(
                (t) => t.targetCustomer.id === loggedInCustomer._id,
              )?.preventDirectAccept || false,
            ),
          ),
        ];

        const updatedJob = { ...preparedJob.job };
        updatedJob.invitees = [...(updatedJob.invitees || []), ...newInvitees];
        await this.jobService.updateJob(
          updatedJob,
          updatedJob.customerInternalInformation || {},
          loggedInCustomer,
          await firstValueFrom(
            this.loggedInUser$.pipe(takeUntil(this.destroyed$)),
          ),
        );
      }

      // Show success message based on what was submitted
      if (totalInviteCount > 0 && totalBookCount > 0) {
        this.toastService.add({
          type: 'SUCCESS',
          message: 'multidayUsersInvitedAndBooked',
          messageOptions: {
            inviteCount: totalInviteCount.toString(),
            bookCount: totalBookCount.toString(),
          },
        });
      } else if (totalInviteCount > 0) {
        this.toastService.add({
          type: 'SUCCESS',
          message: 'multidayUsersInvited',
          messageOptions: {
            count: totalInviteCount.toString(),
          },
        });
      } else if (totalBookCount > 0) {
        this.toastService.add({
          type: 'SUCCESS',
          message: 'multidayUsersBooked',
          messageOptions: {
            count: totalBookCount.toString(),
          },
        });
      }

      this.clearAllSelections();
    } catch (error) {
      console.error('Error submitting selections:', error);
      this.toastService.add({
        type: 'ERROR',
        message: 'serverError',
      });
    }
  }

  private createJobSummary(
    preparedJob: PreparedJob,
    previousSummary: MultiDayJobSummary | undefined,
    loggedInCustomer: Customer,
    sharedPoolCustomers: Customer[],
    selectionState: SelectionState,
    userCountMap: Map<string, number>,
  ): MultiDayJobSummary {
    const { job, possibleUsers, customerOfJob } = preparedJob;
    const jobSelections = selectionState.get(job._id);

    // Sort possible users by their available count (descending - users available for more jobs first)
    const sortedPossibleUsers = [...possibleUsers].sort((a, b) => {
      const countA = userCountMap.get(a._id) || 0;
      const countB = userCountMap.get(b._id) || 0;
      return countB - countA; // Descending order
    });

    // Sort invitees by status first, then by available count
    const sortedInvitees = filterMultidayOverviewInvitees(job).sort((a, b) => {
      // If same status, sort by available count (descending - more available jobs first)
      if (a.userId && b.userId) {
        const countA = userCountMap.get(a.userId) || 0;
        const countB = userCountMap.get(b.userId) || 0;
        return countB - countA;
      }

      return 0;
    });

    return {
      job,
      jobCounts: getJobCounts(job, loggedInCustomer, sharedPoolCustomers),
      jobCustomer: customerOfJob,
      overviewInvitees: sortedInvitees,
      possibleUsers: sortedPossibleUsers,
      directInvitedUsers: jobSelections
        ? sortedPossibleUsers.filter((user) =>
            jobSelections.inviteUserIds.has(user._id),
          )
        : [],
      directAcceptedUsers: jobSelections
        ? sortedPossibleUsers.filter((user) =>
            jobSelections.bookUserIds.has(user._id),
          )
        : [],
      jobInPast: new Date(job.date) < new Date(),
      suggestInsteadDirectAccept: !!job.transfers?.find(
        (transfer) => transfer.targetCustomer.id === loggedInCustomer._id,
      )?.preventDirectAccept,
      collapsed: previousSummary?.collapsed ?? true,
    };
  }
}
